import { useCallback, useEffect, useRef, type SVGProps } from 'react';

const EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)';
const EASE_IN_OUT = 'cubic-bezier(0.77, 0, 0.175, 1)';
const SPIN_TURNS = 3;

type BrandSvgProps = Omit<SVGProps<SVGSVGElement>, 'children'>;

export function GCameraFrameSvg({ className, ...props }: BrandSvgProps) {
  return <svg className={className} viewBox="0 0 240 240" fill="none" {...props}>
    <path
      className="gcam-brand-frame-outer"
      d="M66 26H174C198 26 214 42 214 66V174C214 198 198 214 174 214H66C42 214 26 198 26 174V66C26 42 42 26 66 26Z"
      pathLength="1"
    />
  </svg>;
}

export function GCameraGlyphSvg({ className, ...props }: BrandSvgProps) {
  return <svg className={className} viewBox="0 0 240 240" fill="none" {...props}>
    <path
      className="gcam-brand-g-path"
      d="M170 72H106C84 72 72 84 72 106V142C72 164 84 176 106 176H170V126H132"
    />
  </svg>;
}

export function GCameraWordmarkSvg({ className, ...props }: BrandSvgProps) {
  return <svg className={className} viewBox="0 0 530 120" fill="none" {...props}>
    <g className="gcam-brand-wordmark-paths">
      <path d="M78 20H38Q18 20 18 40V80Q18 100 38 100H78" />
      <path d="M158 50H132Q108 50 108 74V78Q108 100 132 100H158V50M113 76H158" />
      <path d="M190 100V50H210Q232 50 232 70V100M232 70Q232 50 254 50H258Q278 50 278 70V100" />
      <path d="M370 76H310V72Q310 50 332 50H348Q370 50 370 72V76M310 76V80Q310 100 332 100H368" />
      <path d="M402 100V50H420Q440 50 440 70V76" />
      <path d="M512 50H486Q462 50 462 74V78Q462 100 486 100H512V50M467 76H512" />
    </g>
  </svg>;
}

type OrientationPermission = 'granted' | 'denied';
type PermissionedDeviceOrientationEvent = typeof DeviceOrientationEvent & {
  requestPermission?: () => Promise<OrientationPermission>;
};

interface Tilt {
  x: number;
  y: number;
  z: number;
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

export function GCameraBrandLogo() {
  const tiltLayerRef = useRef<HTMLSpanElement>(null);
  const spinLayerRef = useRef<HTMLSpanElement>(null);
  const targetTiltRef = useRef<Tilt>({ x: 0, y: 0, z: 0 });
  const currentTiltRef = useRef<Tilt>({ x: 0, y: 0, z: 0 });
  const baseOrientationRef = useRef<{ beta: number; gamma: number } | null>(null);
  const tiltFrameRef = useRef<number | null>(null);
  const spinAnimationRef = useRef<Animation | null>(null);
  const orientationPermissionRef = useRef<'unknown' | OrientationPermission>('unknown');
  const reduceMotionRef = useRef(false);

  useEffect(() => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    reduceMotionRef.current = reducedMotion.matches;

    const updateMotionPreference = (event: MediaQueryListEvent) => {
      reduceMotionRef.current = event.matches;
      if (!event.matches) return;
      targetTiltRef.current = { x: 0, y: 0, z: 0 };
      currentTiltRef.current = { x: 0, y: 0, z: 0 };
      if (tiltLayerRef.current) tiltLayerRef.current.style.transform = 'rotateX(0deg) rotateY(0deg) rotateZ(0deg)';
    };

    const renderTilt = () => {
      const current = currentTiltRef.current;
      const target = targetTiltRef.current;
      const next = {
        x: current.x + (target.x - current.x) * 0.16,
        y: current.y + (target.y - current.y) * 0.16,
        z: current.z + (target.z - current.z) * 0.16,
      };
      currentTiltRef.current = next;
      if (tiltLayerRef.current) {
        tiltLayerRef.current.style.transform = `rotateX(${next.x.toFixed(2)}deg) rotateY(${next.y.toFixed(2)}deg) rotateZ(${next.z.toFixed(2)}deg)`;
      }

      const distance = Math.abs(target.x - next.x) + Math.abs(target.y - next.y) + Math.abs(target.z - next.z);
      tiltFrameRef.current = distance > 0.03 ? window.requestAnimationFrame(renderTilt) : null;
    };

    const queueTiltRender = () => {
      if (tiltFrameRef.current === null) tiltFrameRef.current = window.requestAnimationFrame(renderTilt);
    };

    const handleOrientation = (event: DeviceOrientationEvent) => {
      if (reduceMotionRef.current || event.beta === null || event.gamma === null) return;
      const base = baseOrientationRef.current ?? { beta: event.beta, gamma: event.gamma };
      baseOrientationRef.current = base;

      const betaDelta = clamp(event.beta - base.beta, -45, 45);
      const gammaDelta = clamp(event.gamma - base.gamma, -45, 45);
      const angle = window.screen.orientation?.angle ?? 0;
      const landscape = Math.abs(angle) === 90;
      const pitch = landscape ? gammaDelta : betaDelta;
      const roll = landscape ? -betaDelta : gammaDelta;

      targetTiltRef.current = {
        x: clamp(-pitch * 0.09, -4.25, 4.25),
        y: clamp(roll * 0.11, -5, 5),
        z: clamp(roll * 0.025, -1.25, 1.25),
      };
      queueTiltRender();
    };

    reducedMotion.addEventListener('change', updateMotionPreference);
    window.addEventListener('deviceorientation', handleOrientation, { passive: true });
    return () => {
      reducedMotion.removeEventListener('change', updateMotionPreference);
      window.removeEventListener('deviceorientation', handleOrientation);
      if (tiltFrameRef.current !== null) window.cancelAnimationFrame(tiltFrameRef.current);
      spinAnimationRef.current?.cancel();
    };
  }, []);

  const requestOrientationPermission = useCallback(async () => {
    if (orientationPermissionRef.current !== 'unknown' || typeof DeviceOrientationEvent === 'undefined') return;
    const orientationEvent = DeviceOrientationEvent as PermissionedDeviceOrientationEvent;
    if (!orientationEvent.requestPermission) {
      orientationPermissionRef.current = 'granted';
      return;
    }
    try {
      orientationPermissionRef.current = await orientationEvent.requestPermission();
    } catch {
      orientationPermissionRef.current = 'denied';
    }
  }, []);

  const spinGlyph = useCallback(() => {
    void requestOrientationPermission();
    const layer = spinLayerRef.current;
    if (!layer) return;
    if (spinAnimationRef.current?.playState === 'running') {
      spinAnimationRef.current.reverse();
      return;
    }

    const frames: Keyframe[] = reduceMotionRef.current
      ? [
          { opacity: 1, filter: 'brightness(1)' },
          { opacity: 0.76, filter: 'brightness(1.2)' },
          { opacity: 1, filter: 'brightness(1)' },
        ]
      : [
          { offset: 0, transform: 'rotateY(0deg) scale(1)', filter: 'brightness(1)' },
          { offset: 1 / 12, transform: 'rotateY(90deg) scale(.965)', filter: 'brightness(.56)' },
          { offset: 2 / 12, transform: 'rotateY(180deg) scale(.985)', filter: 'brightness(.78)' },
          { offset: 3 / 12, transform: 'rotateY(270deg) scale(.965)', filter: 'brightness(.56)' },
          { offset: 4 / 12, transform: 'rotateY(360deg) scale(1)', filter: 'brightness(1.06)' },
          { offset: 5 / 12, transform: 'rotateY(450deg) scale(.965)', filter: 'brightness(.56)' },
          { offset: 6 / 12, transform: 'rotateY(540deg) scale(.985)', filter: 'brightness(.78)' },
          { offset: 7 / 12, transform: 'rotateY(630deg) scale(.965)', filter: 'brightness(.56)' },
          { offset: 8 / 12, transform: 'rotateY(720deg) scale(1)', filter: 'brightness(1.06)' },
          { offset: 9 / 12, transform: 'rotateY(810deg) scale(.965)', filter: 'brightness(.56)' },
          { offset: 10 / 12, transform: 'rotateY(900deg) scale(.985)', filter: 'brightness(.78)' },
          { offset: 11 / 12, transform: 'rotateY(990deg) scale(.965)', filter: 'brightness(.56)' },
          { offset: 1, transform: `rotateY(${SPIN_TURNS * 360}deg) scale(1)`, filter: 'brightness(1)' },
        ];
    spinAnimationRef.current = layer.animate(frames, {
      duration: reduceMotionRef.current ? 180 : 1120,
      easing: reduceMotionRef.current ? EASE_OUT : EASE_IN_OUT,
    });
    spinAnimationRef.current.onfinish = () => {
      spinAnimationRef.current = null;
    };
  }, [requestOrientationPermission]);

  return <h1 className="gcam-brand-logo" aria-label="G Camera">
    <button
      className="gcam-brand-mark-button"
      type="button"
      onClick={spinGlyph}
      aria-label="翻轉 G 標誌"
      title="翻轉 G 標誌"
    >
      <GCameraFrameSvg className="gcam-brand-frame-svg" aria-hidden="true" />
      <span className="gcam-brand-g-intro" aria-hidden="true">
        <span className="gcam-brand-g-tilt" ref={tiltLayerRef}>
          <span className="gcam-brand-g-spin" ref={spinLayerRef}>
            <GCameraGlyphSvg className="gcam-brand-g-svg gcam-brand-g-front" />
            <GCameraGlyphSvg className="gcam-brand-g-svg gcam-brand-g-back" />
          </span>
        </span>
      </span>
    </button>
    <GCameraWordmarkSvg className="gcam-brand-wordmark-svg" aria-hidden="true" />
  </h1>;
}
